import { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import OfferPage from "@/pages/OfferPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import IntakePage from "@/pages/IntakePage";
import SchedulePage from "@/pages/SchedulePage";
import ThankYouPage from "@/pages/ThankYouPage";
import PortalPage from "@/pages/PortalPage";
import AdminPage from "@/pages/AdminPage";
import ProductViewPage from "@/pages/ProductViewPage";
import AssessmentLandingPage from "@/pages/AssessmentLandingPage";
import QuizPage from "@/pages/QuizPage";
import QuizResultPage from "@/pages/QuizResultPage";
import RefundPolicyPage from "@/pages/RefundPolicyPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import Seo from "@/components/Seo";

const HOME_TITLE = "All In Hand Marketing — Assess. Build. Grow. Scale.";
const HOME_DESC =
  "Strategy, branding, digital marketing and automation for small business. Start with an AI-drafted Growth Assessment, a $9.99 Business Snapshot or a free check.";

const SEO_MAP = {
  "/": { title: HOME_TITLE, description: HOME_DESC, path: "/" },
  "/go": { title: HOME_TITLE, description: HOME_DESC, path: "/" },
  "/assessment": {
    title: "Business Growth Assessment — All In Hand Marketing",
    description:
      "Find out what's stopping your business from growing. A full review with findings, priorities and a human-approved report.",
    path: "/assessment",
  },
  "/quiz": {
    title: "Free Marketing Foundations Check — All In Hand Marketing",
    description:
      "Ten quick questions, a score out of 100 and the one gap worth fixing first. Free, about two minutes, no package required.",
    path: "/quiz",
  },
  "/refund-policy": {
    title: "Refund Policy — All In Hand Marketing",
    description: "How refunds work for assessments, digital products and services from All In Hand Marketing.",
    path: "/refund-policy",
  },
  "/terms": { title: "Terms of Service — All In Hand Marketing", path: "/terms" },
  "/privacy": { title: "Privacy Policy — All In Hand Marketing", path: "/privacy" },
};

const PRIVATE_TITLES = {
  "/quiz/result": "Your Foundations Check Result",
  "/payment/success": "Payment Successful",
  "/payment/cancel": "Payment Cancelled",
  "/intake": "Complete Your Intake",
  "/schedule": "Book Your Strategy Session",
  "/thank-you": "Thank You",
  "/portal": "Customer Portal",
  "/admin": "Command Center",
  "/product-view": "Your Product",
};

// Per-route titles/descriptions; every route not in SEO_MAP (accounts, checkout,
// delivery, admin) is noindex'd so only public marketing pages get indexed.
const RouteSeo = () => {
  const { pathname } = useLocation();
  const clean = pathname.replace(/\/+$/, "") || "/";
  const pub = SEO_MAP[clean];
  if (pub) return <Seo title={pub.title} description={pub.description} path={pub.path} />;
  return <Seo title={`${PRIVATE_TITLES[clean] || "Page Not Found"} — All In Hand Marketing`} noindex />;
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-obsidian flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-4">All In Hand Marketing</p>
            <h1 className="font-display text-3xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-slate-400 mb-8">Please reload the page to continue.</p>
            <button
              data-testid="error-reload-button"
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-full bg-gold text-obsidian font-semibold hover:bg-gold-bright transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function NotFound() {
  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-8">
      <div className="text-center">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-4">404</p>
        <h1 className="font-display text-4xl font-bold text-white mb-4">Page not found</h1>
        <Link
          data-testid="notfound-home-link"
          to="/"
          className="inline-block px-6 py-3 rounded-full bg-gold text-obsidian font-semibold hover:bg-gold-bright transition-colors"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <RouteSeo />
        <Routes>
          <Route path="/" element={<OfferPage />} />
          <Route path="/go" element={<OfferPage />} />
          <Route path="/assessment" element={<AssessmentLandingPage />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/quiz/result" element={<QuizResultPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/intake" element={<IntakePage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/portal" element={<PortalPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/product-view" element={<ProductViewPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
